/**
 * The O Collection — base theme interactions:
 * scroll-reveal, sticky header state, and PDP variant swatches.
 * (Nav-board open/close and its flap-tile animation live in split-flap.js,
 * since they're tightly coupled to the tile-building logic.)
 */
(function () {
  "use strict";

  function initScrollReveal() {
    var targets = document.querySelectorAll("[data-reveal], [data-reveal-stagger]");
    if (!targets.length) return;

    if (!("IntersectionObserver" in window)) {
      targets.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  function initStickyHeader() {
    var header = document.querySelector("[data-site-header]");
    if (!header) return;

    // Pages with a full-bleed "transparent zone" (the homepage hero, or a
    // collection banner) start transparent and solidify on scroll; every
    // other page (product, cart...) has nothing dark to blend with, so it
    // starts solid immediately.
    var hasTransparentZone = !!document.querySelector("[data-header-transparent-zone]");
    var threshold = 60;

    function update() {
      if (!hasTransparentZone || window.scrollY > threshold) {
        header.classList.add("is-solid");
      } else {
        header.classList.remove("is-solid");
      }
    }

    window.addEventListener("scroll", update, { passive: true });
    update();
  }

  function initVariantSwatches(gallery) {
    var info = document.querySelector("[data-product-info]");
    if (!info) return;

    var dataEl = info.querySelector("[data-variant-data]");
    var variants = [];
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
    }

    var groups = info.querySelectorAll("[data-variant-group]");
    var idInput = info.querySelector("[data-variant-id]");
    var priceEl = info.querySelector("[data-price]");
    var addBtn = info.querySelector("[data-add-to-cart]");
    var addText = info.querySelector("[data-add-to-cart-text]");

    // The gemstone option (if any) drives the gallery's photo filtering.
    var stoneGroup = info.querySelector("[data-stone-group]");
    var stonePos = stoneGroup ? stoneGroup.getAttribute("data-option-position") : null;

    // Selected value per option position, e.g. { 1: "Emerald", 2: "6.5" }.
    var selected = {};
    groups.forEach(function (group) {
      var pos = group.getAttribute("data-option-position");
      var pressed = group.querySelector('[data-variant-option][aria-pressed="true"]') ||
                    group.querySelector("[data-variant-option]");
      if (pressed) selected[pos] = pressed.getAttribute("data-value");
    });

    function findVariant() {
      var positions = Object.keys(selected);
      return variants.find(function (v) {
        return positions.every(function (pos) {
          // variant.options is ordered by option position (index 0 = position 1).
          return v.options[parseInt(pos, 10) - 1] === selected[pos];
        });
      });
    }

    function syncGallery(variant) {
      if (!gallery || stonePos == null) return;
      var leadMedia = variant ? variant.featured_media : null;
      gallery.filter(selected[stonePos], leadMedia);
    }

    function update() {
      var variant = findVariant();

      if (variant) {
        if (idInput) idInput.value = variant.id;
        if (priceEl && variant.price) priceEl.textContent = variant.price;
        if (addBtn) addBtn.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? t("add_to_cart", "Add to cart") : t("sold_out", "Sold out");
      } else if (addBtn) {
        // Selected combination has no matching variant (unavailable pairing).
        addBtn.disabled = true;
        if (addText) addText.textContent = t("sold_out", "Sold out");
      }

      syncGallery(variant);
    }

    groups.forEach(function (group) {
      var pos = group.getAttribute("data-option-position");
      var buttons = group.querySelectorAll("[data-variant-option]");
      var valueLabel = group.querySelector("[data-option-value]");
      buttons.forEach(function (btn) {
        btn.addEventListener("click", function () {
          buttons.forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
          btn.setAttribute("aria-pressed", "true");
          selected[pos] = btn.getAttribute("data-value");
          if (valueLabel) valueLabel.textContent = btn.getAttribute("data-value");
          update();
        });
      });
    });

    // Filter the gallery to the initially selected gem on load. Runs the
    // gallery sync only (not the full update), so a "From $X" price stays put.
    syncGallery(findVariant());
  }

  // Read a localized product string if the theme exposes one, else fall back.
  function t(key, fallback) {
    try {
      if (window.theme && window.theme.strings && window.theme.strings[key]) {
        return window.theme.strings[key];
      }
    } catch (e) {}
    return fallback;
  }

  // On desktop, match the info card height to the gallery so the two
  // columns stay level; long copy scrolls inside the card instead of
  // pushing it taller than the imagery.
  function initInfoHeightSync() {
    var info = document.querySelector("[data-product-info]");
    var gallery = document.querySelector("[data-product-gallery]");
    if (!info || !gallery) return;

    var mq = window.matchMedia("(min-width: 900px)");

    function sync() {
      if (mq.matches) {
        info.classList.add("is-synced");
        info.style.height = gallery.offsetHeight + "px";
      } else {
        info.classList.remove("is-synced");
        info.style.height = "";
      }
    }

    sync();
    window.addEventListener("resize", sync);
    if ("ResizeObserver" in window) {
      new ResizeObserver(sync).observe(gallery);
    } else {
      // Re-sync once imagery has loaded and settled the gallery height.
      window.addEventListener("load", sync);
    }
  }

  // Builds one PDP gallery and returns a controller so the variant selector can
  // filter it by gemstone. `filter(value, leadMediaId)` narrows the visible
  // media to the photos tagged with that gem (plus untagged "shared" shots),
  // leads with the variant's assigned photo, and hides everything else.
  function initProductGallery() {
    var gallery = document.querySelector("[data-product-gallery]");
    if (!gallery) return null;

    var stageImages = toArray(gallery.querySelectorAll("[data-gallery-image]"));
    var thumbs = toArray(gallery.querySelectorAll("[data-gallery-thumb]"));
    var prevBtn = gallery.querySelector("[data-gallery-prev]");
    var nextBtn = gallery.querySelector("[data-gallery-next]");
    if (!stageImages.length) return null;

    // Pair each stage image with its thumbnail (same product-media order) and
    // parse the gem membership emitted by the pdp-media-stones snippet.
    var items = stageImages.map(function (img, i) {
      return {
        stage: img,
        thumb: thumbs[i] || null,
        mediaId: img.getAttribute("data-media-id"),
        values: parseValues(img.getAttribute("data-media-values"))
      };
    });

    var order = items.slice(); // currently visible items, in display order
    var current = 0;

    function parseValues(str) {
      if (!str) return [];
      return str.split("|").map(function (s) { return s.trim(); })
        .filter(function (s) { return s.length > 0; });
    }

    function show(index) {
      if (!order.length) return;
      current = (index + order.length) % order.length;
      var active = order[current];
      items.forEach(function (it) {
        var pos = order.indexOf(it);
        var visible = pos !== -1;
        it.stage.hidden = !visible;
        it.stage.classList.toggle("is-active", it === active);
        if (it.thumb) {
          it.thumb.hidden = !visible;
          it.thumb.classList.toggle("is-active", it === active);
          if (visible) it.thumb.style.order = pos;
        }
      });
    }

    function filter(value, leadMediaId) {
      var v = value ? String(value).toLowerCase().trim() : "";
      var matched = items.filter(function (it) { return it.values.indexOf(v) !== -1; });
      var shared = items.filter(function (it) { return it.values.length === 0; });
      var anyTagged = items.some(function (it) { return it.values.length > 0; });

      if (!anyTagged) {
        // Product isn't organized by gem at all — show everything as-is
        // rather than blanking the gallery.
        order = items.slice();
      } else if (matched.length) {
        // Show ONLY the selected gem's photos (each gem gets its own set,
        // including its own lifestyle shots).
        order = matched;
      } else {
        // This gem has no tagged photos yet — fall back to shared, else all.
        order = shared.length ? shared : items.slice();
      }

      // Lead with the variant's assigned photo when it's in the visible set.
      var leadIndex = 0;
      if (leadMediaId != null) {
        for (var i = 0; i < order.length; i++) {
          if (String(order[i].mediaId) === String(leadMediaId)) { leadIndex = i; break; }
        }
      }
      show(leadIndex);
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        var pos = order.indexOf(items[i]);
        if (pos !== -1) show(pos);
      });
    });
    if (prevBtn) prevBtn.addEventListener("click", function () { show(current - 1); });
    if (nextBtn) nextBtn.addEventListener("click", function () { show(current + 1); });

    // Basic swipe support on the stage for touch devices.
    var stage = gallery.querySelector(".pdp__stage");
    var touchStartX = null;
    if (stage) {
      stage.addEventListener("touchstart", function (e) {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });
      stage.addEventListener("touchend", function (e) {
        if (touchStartX === null) return;
        var delta = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) > 40) show(current + (delta < 0 ? 1 : -1));
        touchStartX = null;
      }, { passive: true });
    }

    show(0);

    return { filter: filter };
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  // Progressive enhancement: turn the native ring-size <select> into an
  // odometer / gas-pump rolling wheel. The <select> stays in the DOM as the
  // form's value carrier; if this never runs, the plain select still works.
  function initRingSizeWheel() {
    var wraps = document.querySelectorAll("[data-size-wheel]");
    wraps.forEach(function (wrap) {
      var select = wrap.querySelector(".pdp__size-select");
      if (!select || select.options.length < 2) return;

      // Size scales keyed by unit. The customer toggles which one is shown; the
      // chosen value carries its unit (e.g. "US 7" / "EU 54") onto the order.
      var UNIT_LABELS = { us: "US", eu: "EU" };
      function parseList(attr) {
        return (wrap.getAttribute(attr) || "")
          .split(",")
          .map(function (s) { return s.trim(); })
          .filter(function (s) { return s !== ""; });
      }
      var lists = { us: parseList("data-sizes-us"), eu: parseList("data-sizes-eu") };
      var toggle = wrap.querySelector("[data-size-toggle]");
      var placeholder = select.options[0] ? select.options[0].textContent : "Select size";
      var mode = "us";

      var wheel = document.createElement("div");
      wheel.className = "pdp__size-wheel";
      wheel.setAttribute("role", "listbox");
      wheel.setAttribute("tabindex", "0");
      wheel.setAttribute("aria-label", labelFor(wrap, select));

      var track = document.createElement("div");
      track.className = "pdp__size-wheel__track";
      wheel.appendChild(track);

      // Rebuilt whenever the unit changes.
      var items = [];
      var count = 0;

      // Reflow the native <select> (form value carrier) to a flat placeholder +
      // one unit's options, so wheel index maps 1:1 to select.selectedIndex.
      function populateSelect(unit) {
        var frag = '<option value="" disabled selected>' + placeholder + "</option>";
        lists[unit].forEach(function (tok) {
          frag += '<option value="' + UNIT_LABELS[unit] + " " + tok + '">' + tok + "</option>";
        });
        select.innerHTML = frag;
      }

      function buildTrack() {
        var opts = toArray(select.options);
        track.innerHTML = "";
        items = opts.map(function (opt, i) {
          var el = document.createElement("div");
          el.className = "pdp__size-wheel__item";
          el.setAttribute("role", "option");
          el.textContent = opt.value === "" ? "–" : opt.text;
          el.setAttribute("data-index", i);
          el.addEventListener("click", function () { setIndex(i); });
          track.appendChild(el);
          return el;
        });
        count = items.length;
      }

      function setMode(unit, animate) {
        if (!lists[unit] || !lists[unit].length) return;
        mode = unit;
        populateSelect(unit);
        buildTrack();
        index = 0;
        wrap.classList.remove("pdp__size--error");
        if (toggle) {
          toArray(toggle.querySelectorAll("[data-unit]")).forEach(function (b) {
            var on = b.getAttribute("data-unit") === unit;
            b.classList.toggle("is-active", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
          });
        }
        render(animate !== false);
      }

      wheel.insertAdjacentHTML(
        "beforeend",
        '<div class="pdp__size-wheel__band"></div>' +
        '<div class="pdp__size-wheel__mask"></div>' +
        '<button type="button" class="pdp__size-wheel__btn pdp__size-wheel__btn--up" aria-label="Previous size" tabindex="-1">' + chevron("up") + "</button>" +
        '<button type="button" class="pdp__size-wheel__btn pdp__size-wheel__btn--down" aria-label="Next size" tabindex="-1">' + chevron("down") + "</button>"
      );

      var error = document.createElement("p");
      error.className = "pdp__size__error";
      error.setAttribute("aria-live", "polite");
      error.textContent = "Please choose a ring size.";

      select.parentNode.insertBefore(wheel, select.nextSibling);
      wheel.parentNode.insertBefore(error, wheel.nextSibling);
      wrap.classList.add("pdp__size--wheel-active");

      var index = 0;

      function itemHeight() {
        var v = getComputedStyle(wheel).getPropertyValue("--wheel-item-h");
        return parseFloat(v) || 46;
      }
      function centerOffset(h) { return h; } // (visible 3 − 1) / 2 rows

      function place(y, animate) {
        track.style.transition = animate ? "" : "none";
        track.style.transform = "translateY(" + y + "px)";
      }
      function render(animate) {
        var h = itemHeight();
        place(centerOffset(h) - index * h, animate);
        items.forEach(function (el, i) {
          var dist = Math.abs(i - index);
          el.style.opacity = Math.max(0.16, 1 - dist * 0.34);
          el.style.transform = "scale(" + Math.max(0.72, 1 - dist * 0.13) + ")";
          el.setAttribute("aria-selected", i === index ? "true" : "false");
        });
      }
      function setIndex(i, animate) {
        index = Math.min(count - 1, Math.max(0, i));
        select.selectedIndex = index;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        if (select.value !== "") wrap.classList.remove("pdp__size--error");
        render(animate !== false);
      }

      wheel.querySelector(".pdp__size-wheel__btn--up").addEventListener("click", function () { setIndex(index - 1); });
      wheel.querySelector(".pdp__size-wheel__btn--down").addEventListener("click", function () { setIndex(index + 1); });

      if (toggle) {
        toggle.addEventListener("click", function (e) {
          var btn = e.target.closest("[data-unit]");
          if (!btn) return;
          var unit = btn.getAttribute("data-unit");
          if (unit !== mode) setMode(unit);
        });
      }

      var wheelLock = false;
      wheel.addEventListener("wheel", function (e) {
        e.preventDefault();
        if (wheelLock) return;
        wheelLock = true;
        setTimeout(function () { wheelLock = false; }, 110);
        setIndex(index + (e.deltaY > 0 ? 1 : -1));
      }, { passive: false });

      wheel.addEventListener("keydown", function (e) {
        if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); setIndex(index + 1); }
        else if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); setIndex(index - 1); }
        else if (e.key === "Home") { e.preventDefault(); setIndex(0); }
        else if (e.key === "End") { e.preventDefault(); setIndex(count - 1); }
      });

      // Drag to spin.
      var dragging = false, startY = 0, baseY = 0;
      wheel.addEventListener("pointerdown", function (e) {
        // Let the up/down arrows fire their own click. Capturing the pointer
        // here would retarget the click to the wheel and swallow it.
        if (e.target.closest(".pdp__size-wheel__btn")) return;
        dragging = true;
        startY = e.clientY;
        var h = itemHeight();
        baseY = centerOffset(h) - index * h;
        wheel.classList.add("is-grabbing");
        wheel.setPointerCapture(e.pointerId);
      });
      wheel.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        place(baseY + (e.clientY - startY), false);
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        wheel.classList.remove("is-grabbing");
        var h = itemHeight();
        var current = parseFloat((track.style.transform.match(/-?[\d.]+/) || [centerOffset(h)])[0]);
        setIndex(Math.round((centerOffset(h) - current) / h));
      }
      wheel.addEventListener("pointerup", endDrag);
      wheel.addEventListener("pointercancel", endDrag);

      // Block add-to-cart until a size is chosen (native required is skipped
      // because the select is display:none once enhanced).
      var form = select.closest("form");
      if (form) {
        form.addEventListener("submit", function (e) {
          if (select.value === "") {
            e.preventDefault();
            wrap.classList.add("pdp__size--error");
            wheel.focus();
          }
        });
      }

      setMode(mode, false);
      window.addEventListener("resize", function () { render(false); });
    });
  }

  function labelFor(wrap, select) {
    var lbl = wrap.querySelector("label");
    return (lbl && lbl.textContent.trim()) || select.getAttribute("aria-label") || "Ring size";
  }

  function chevron(dir) {
    var d = dir === "up" ? "M1 5.5L6 1L11 5.5" : "M1 1L6 5.5L11 1";
    return '<svg width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true"><path d="' + d + '" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function initProductCardHover() {
    // touch devices: tap once to preview second image, tap again on card link to visit
    var cards = document.querySelectorAll(".product-card__media");
    cards.forEach(function (media) {
      media.addEventListener("touchstart", function () {
        media.classList.toggle("is-previewing");
      }, { passive: true });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initScrollReveal();
    initStickyHeader();
    initVariantSwatches(initProductGallery());
    initInfoHeightSync();
    initRingSizeWheel();
    initProductCardHover();
  });
})();
